import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Search, Users, X } from "lucide-react";
import InputCst from "@components/InputCst/InputCst";
import ListItemCardCst from "@components/ListItemCardCst";
import PaginatorCst from "@components/PaginatorCst";
import SortMenuCst from "@components/SortMenuCst";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { ServicesURL } from "@utils/utilsApi";
import { openToast } from "@utils/toastStore";
import { updateUserStaticGroups } from "../updateUserStaticGroups";

const PAGE_SIZE = 10;
const MEMBERS_PAGE_SIZE = 1000;

interface AddUsersToGroupModalProps {
  open: boolean;
  onClose: () => void;
  corporateId: string;
  group?: { id: number | string; name?: string } | null;
  onSuccess?: () => void;
}

interface ContentProps {
  onClose: () => void;
  corporateId: string;
  group: { id: number | string; name?: string };
  onSuccess?: () => void;
}

const AddUsersToGroupModalContent: React.FC<ContentProps> = ({
  onClose,
  corporateId,
  group,
  onSuccess,
}) => {
  const { t } = useTranslation("users-groups-v2");
  const theme = useTheme();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<string | null>("most-recent");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const membersUrl =
    corporateId && group.id
      ? `${ServicesURL.staticGroups}/v3/${corporateId}/static/${group.id}/users`
      : null;
  const {
    items: members,
    isLoading: isLoadingMembers,
    mutate: mutateMembers,
  } = usePaginatedList(
    membersUrl,
    { page: 1, pageSize: MEMBERS_PAGE_SIZE },
    { responseKey: "response" },
  );

  const initialIds = useMemo(
    () =>
      (members || [])
        .map((m: any) => Number(m.user_id ?? m.id_user ?? m.id))
        .filter((idUser: number) => !Number.isNaN(idUser)),
    [members],
  );

  const dirtyRef = useRef(false);
  const initialIdsKey = initialIds.join(",");
  useEffect(() => {
    if (dirtyRef.current || isLoadingMembers) return;
    setSelected(new Set(initialIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialIdsKey, isLoadingMembers]);

  const listUrl = corporateId
    ? `${ServicesURL.getCustomer}?corporate_id=${corporateId}`
    : null;

  const { items, totalItems, totalPages, isLoading } = usePaginatedList(
    listUrl,
    {
      page,
      pageSize,
      search: search || undefined,
      sort,
    },
  );

  const toggle = (userId: number, next: boolean) => {
    dirtyRef.current = true;
    setSelected((prev) => {
      const copy = new Set(prev);
      if (next) copy.add(userId);
      else copy.delete(userId);
      return copy;
    });
  };

  const { toAdd, toRemove } = useMemo(() => {
    const initialSet = new Set(initialIds);
    return {
      toAdd: Array.from(selected).filter((idUser) => !initialSet.has(idUser)),
      toRemove: initialIds.filter((idUser) => !selected.has(idUser)),
    };
  }, [initialIds, selected]);

  const hasChanges = toAdd.length > 0 || toRemove.length > 0;

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      // TODO (BE Mancante): sostituire il ciclo di PUT per-utente con
      // l'endpoint bulk (userIds sul gruppo) quando disponibile.
      const ops = [
        ...toAdd.map((idUser) =>
          updateUserStaticGroups(idUser, {
            addGroupIds: [Number(group.id)],
            removeGroupIds: [],
          }),
        ),
        ...toRemove.map((idUser) =>
          updateUserStaticGroups(idUser, {
            addGroupIds: [],
            removeGroupIds: [Number(group.id)],
          }),
        ),
      ];
      const results = await Promise.allSettled(ops);
      const failed = results.filter((r) => r.status === "rejected").length;
      mutateMembers();
      onSuccess?.();
      onClose();
      if (failed > 0) {
        openToast({
          severity: "error",
          message: String(
            t("add-users-modal.error-partial", {
              done: ops.length - failed,
              total: ops.length,
            }),
          ),
        });
      } else {
        openToast({
          severity: "success",
          message: String(t("add-users-modal.success")),
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const sortOptions = [
    { label: t("sort.mostRecent"), value: "most-recent" },
    { label: t("sort.leastRecent"), value: "least-recent" },
    { label: t("sort.alphabetical"), value: "alphabetical" },
  ];

  return (
    <>
      <DialogTitle sx={{ padding: "20px 24px 0" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                color: theme.palette.text.primary,
              }}
            >
              <Users size={16} />
              <Typography sx={{ fontSize: "13px" }}>
                {group.name || group.id}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 700,
                color: theme.palette.text.primary,
                mt: 0.5,
              }}
            >
              {t("add-users-modal.title")}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="close"
            disabled={isSubmitting}
            sx={{ color: theme.palette.text.primary, flexShrink: 0 }}
          >
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "0 24px 8px",
          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
          "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
        }}
      >
        <Box
          sx={{ pt: "16px", display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 200,
                "& .MuiFormHelperText-root": { display: "none" },
              }}
            >
              <InputCst
                id="add-users-to-group-search"
                label={t("add-users-modal.search") || ""}
                value={search}
                onChange={(e: any) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                endAdornment={
                  <Search size={16} color={theme.palette.text.tertiary} />
                }
              />
            </Box>
            <SortMenuCst
              id="add-users-to-group-sort"
              value={sort ?? "most-recent"}
              options={sortOptions}
              onChange={(next) => {
                setSort(next || null);
                setPage(1);
              }}
            />
          </Box>

          <Typography
            sx={{ fontSize: "13px", color: theme.palette.text.primary }}
          >
            {t("add-users-modal.selected-count", { count: selected.size })}
          </Typography>

          {isLoading || isLoadingMembers ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 5,
                color: theme.palette.text.primary,
              }}
            >
              {t("add-users-modal.empty")}
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {items.map((user: any) => {
                const fullName = [user.name, user.surname]
                  .filter(Boolean)
                  .join(" ")
                  .trim();
                return (
                  <ListItemCardCst
                    key={user.id_user}
                    title={fullName || user.email || String(user.id_user)}
                    subtitle={{ id: user.id_user, activityType: user.email }}
                    badges={
                      user.status === "active"
                        ? [
                            {
                              label: t("add-users-modal.active"),
                              variant: "success",
                            },
                          ]
                        : []
                    }
                    actions={[]}
                    selectable
                    selected={selected.has(Number(user.id_user))}
                    onSelect={(next) => toggle(Number(user.id_user), next)}
                  />
                );
              })}
            </Box>
          )}

          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <PaginatorCst
                page={page}
                pageSize={pageSize}
                totalItems={totalItems}
                onPageChange={setPage}
                onPageSizeChange={(next) => {
                  setPageSize(next);
                  setPage(1);
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: "12px 24px 20px" }}>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isSubmitting || !hasChanges}
          sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 700 }}
        >
          {isSubmitting
            ? t("add-users-modal.submitting")
            : t("add-users-modal.confirm")}
        </Button>
      </DialogActions>
    </>
  );
};

const AddUsersToGroupModal: React.FC<AddUsersToGroupModalProps> = ({
  open,
  onClose,
  corporateId,
  group,
  onSuccess,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      {open && group && (
        <AddUsersToGroupModalContent
          onClose={onClose}
          corporateId={corporateId}
          group={group}
          onSuccess={onSuccess}
        />
      )}
    </Dialog>
  );
};

export default AddUsersToGroupModal;
